/**
 * المرحلة 1.2 — the patient's health profile on the screens: its seven steps,
 * when each counts as done, how complete it is under a policy, and which of a
 * prescription's medicines a recorded allergy names.
 *
 * The same code as Backend/lib/healthProfile.js, so the card on the home
 * screen and the wizard show exactly what the server computes;
 * Tests/suites/frontend/p12-completeness.test.mjs holds the two equal.
 */

export const STEPS = Object.freeze(['basics', 'vitals', 'conditions', 'allergies', 'family', 'reports', 'insurance']);

/** The `healthOnboarding` policy as the server stores it, read safely. */
export function readPolicy(policy) {
  const required = Array.isArray(policy && policy.required) ? policy.required.filter((s) => STEPS.includes(s)) : [];
  const threshold = Math.min(100, Math.max(0, Number(policy && policy.threshold) || 0));
  return { required, threshold };
}

const list = (value) => (Array.isArray(value) ? value : []);
const present = (value) => value !== null && value !== undefined && String(value).trim() !== '';

/**
 * Whether the patient's data already answers a step, before they press save on it.
 *
 * @param {string} step
 * @param {object} facts { user, profile, allergyCount, recordCount }
 */
export function evident(step, { user = {}, profile = {}, allergyCount = 0, recordCount = 0 } = {}) {
  switch (step) {
    case 'basics':
      return present(user.dateOfBirth) && present(user.gender);
    case 'vitals':
      return present(profile.heightCm) && present(profile.weightKg);
    case 'conditions':
      return list(profile.chronicConditions).length > 0 || list(profile.currentMedications).length > 0;
    case 'allergies':
      return allergyCount > 0;
    case 'family':
      return list(profile.familyHistory).length > 0;
    case 'reports':
      return recordCount > 0;
    case 'insurance':
      return present(user.insuranceCardImage) || present(user.insurancePolicyNo);
    default:
      return false;
  }
}

/**
 * Where the profile stands under a policy.
 *
 * The percentage counts the policy's required steps when it names any, and all
 * seven otherwise (the default names none, so a patient sees how far the whole
 * profile is). `ready` is what a booking waits for: every required step done and
 * the percentage at the policy's threshold (0 by default: nothing waits).
 *
 * @returns {{ steps: Array<{id, required, done, skipped}>, completeness: number, ready: boolean, next: string|null, missing: string[] }}
 */
export function standing(facts = {}, policy = {}) {
  const { required, threshold } = readPolicy(policy);
  const profile = facts.profile || {};
  const completed = new Set(list(profile.completedSteps));
  const skipped = new Set(list(profile.skippedSteps));
  const steps = STEPS.map((id) => {
    const done = completed.has(id) || evident(id, facts);
    return { id, required: required.includes(id), done, skipped: !done && skipped.has(id) };
  });
  const counted = required.length ? steps.filter((s) => s.required) : steps;
  const completeness = Math.round((counted.filter((s) => s.done).length / counted.length) * 100);
  const missing = steps.filter((s) => s.required && !s.done).map((s) => s.id);
  const open = steps.filter((s) => !s.done);
  const next = (open.find((s) => !s.skipped) || open[0] || {}).id || null;
  return { steps, completeness, ready: missing.length === 0 && completeness >= threshold, next, missing, threshold };
}

/** The percentage alone: what the profile card and the booking threshold read. */
export function completeness(facts = {}, policy = {}) {
  return standing(facts, policy).completeness;
}

/* ── allergies against medicines ─────────────────────────────────────────── */

/**
 * One spelling of a substance for comparing: lower case, Arabic letter forms
 * unified (أ إ آ → ا, ى → ي, ة → ه), diacritics and tatweel dropped, spaces collapsed.
 */
export function normaliseSubstance(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * A few families whose members a patient's allergy to one name covers, in both
 * languages and by common brand. Deliberately short and conservative: the plan
 * asks a doctor to review the alert rules (Part 11), and until then a family is
 * listed only where an allergy to it is a standing contraindication.
 */
export const FAMILIES = Object.freeze([
  ['penicillin', 'amoxicillin', 'ampicillin', 'amoxiclav', 'augmentin', 'flucloxacillin', 'piperacillin', 'بنسلين', 'اموكسيسيلين', 'امبيسيلين', 'اوجمنتين', 'هاي موكس'],
  ['sulfa', 'sulfonamide', 'sulfamethoxazole', 'septrin', 'bactrim', 'سلفا', 'سبترين'],
  ['aspirin', 'acetylsalicylic', 'aspocid', 'اسبرين', 'اسبوسيد'],
  ['ibuprofen', 'brufen', 'بروفين', 'ايبوبروفين'],
  ['diclofenac', 'cataflam', 'voltaren', 'كتافلام', 'فولتارين', 'ديكلوفيناك'],
].map((names) => Object.freeze(names.map(normaliseSubstance))));

/** Every spelling an allergy to `substance` covers. */
export function coveredNames(substance) {
  const key = normaliseSubstance(substance);
  if (!key) return [];
  const family = FAMILIES.find((names) => names.some((n) => n === key || ` ${key} `.includes(` ${n} `)));
  return family ? Array.from(new Set([key, ...family])) : [key];
}

/**
 * The medicines on a prescription that a recorded allergy names.
 *
 * @param {Array<{name}>} medicines  the prescription's medicines (Prescription.medicines)
 * @param {Array<{substance, severity, id}>} allergies
 * @returns {Array<{ medicine, substance, severity, allergyId }>}
 */
export function allergyMatches(medicines, allergies) {
  const out = [];
  for (const allergy of list(allergies)) {
    const names = coveredNames(allergy.substance).filter((n) => n.length >= 3);
    if (!names.length) continue;
    for (const medicine of list(medicines)) {
      const drug = normaliseSubstance(medicine && (medicine.name || medicine));
      if (!drug) continue;
      // Whole words only: «sulfa» names Septrin, not ferrous sulfate.
      if (names.some((n) => ` ${drug} `.includes(` ${n} `))) {
        out.push({ medicine: String(medicine.name || medicine), substance: allergy.substance, severity: allergy.severity || 'moderate', allergyId: allergy.id ?? null });
      }
    }
  }
  return out;
}
